/**
 * Coach Team Management page (PR-X1, May 2026).
 *
 * Replaces the prior mock-array implementation with real API integration:
 *   - GET /api/batches            — list (active by default)
 *   - GET /api/batches?includeArchived=true — archived tab
 *   - PATCH /api/batches/:id/archive   — soft-delete
 *   - PATCH /api/batches/:id/restore   — un-archive
 *   - Add/Edit modal: AddEditTeamDialog
 *
 * Mobile-first:
 *   - Above md: table view (current desktop layout).
 *   - Below md: stacked card grid (each team is a card with all actions).
 *   - Sticky "Add Team" floating action button below md so coaches always
 *     have a fast action surface from anywhere on the page.
 *
 * Active vs Archived tabs (per product spec). Sort within Active matches
 * server-side order: skillLevel rank → ageGroup → name.
 *
 * Out of scope (deferred):
 *   - currentEnrollment derivation — wired up in PR-X3 with player→team.
 *   - Playing-12 lineup, match-day views — separate PRs.
 *
 * /coach/batches (BatchesPage) is real API-backed now, not mock — it's a
 * genuinely different surface (roster/assignment) from this page (team
 * CRUD/lifecycle), not a duplicate slated for merging. See the real-vs-mock
 * investigation (2026-08-18) in DOCS/sprint-log.md.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Users,
  Plus,
  Edit2,
  Archive,
  ArchiveRestore,
  Award,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MetricTile } from "@/components/parent/MetricTile";
import { asArray, cn } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast, notify } from "@/shared/toast";
import { AddEditTeamDialog, type TeamForEdit } from "@/components/teams/AddEditTeamDialog";

interface BatchRow {
  id: number;
  academyId: number | null;
  name: string;
  ageGroup: string;
  ageRange: string;
  skillLevel: "Beginner" | "Intermediate" | "Advanced" | null;
  coachId: number;
  schedule: string | null;
  venue: string | null;
  maxCapacity: number | null;
  currentEnrollment: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type Tab = "active" | "archived";

function skillBadgeColor(level?: string | null) {
  switch (level) {
    case "Beginner":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "Intermediate":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "Advanced":
      return "bg-orange-100 text-orange-700 border-orange-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

export default function TeamManagement() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamForEdit | undefined>();
  const { toast } = useToast();
  const safeToast = (t: any) => { try { toast(t); } catch { notify(t); } };

  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/batches", tab],
    queryFn: async () => {
      const path =
        tab === "archived"
          ? "/api/batches?includeArchived=true"
          : "/api/batches";
      const res: any = await apiRequest("GET", path);
      return res;
    },
  });

  // Server returns all rows (active+archived) when includeArchived=true; we
  // filter to ONLY archived for the Archived tab so toggling tabs feels right.
  const allRows: BatchRow[] = asArray<BatchRow>(data?.data);
  const teams =
    tab === "archived"
      ? allRows.filter((r) => !r.isActive)
      : allRows.filter((r) => r.isActive);

  // Stopgap until a real Render Cron Job exists — see
  // server/routes/batches.ts's POST /generate-sessions. Idempotent, safe
  // to click repeatedly.
  const generateSessionsMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/batches/generate-sessions"),
    onSuccess: (res: any) => {
      const { totalCreated, totalSkipped, batchCount } = res?.data ?? {};
      safeToast({
        title: "Upcoming sessions generated",
        description:
          totalCreated > 0
            ? `${totalCreated} new session(s) created across ${batchCount} team(s). ${totalSkipped} already existed.`
            : `No new sessions needed — all ${batchCount} team(s) are already up to date.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/coach/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
    },
    onError: (e: Error) =>
      safeToast({
        title: "Failed to generate sessions",
        description: e.message || "Please try again.",
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (batchId: number) =>
      apiRequest("PATCH", `/api/batches/${batchId}/archive`),
    onSuccess: () => {
      safeToast({
        title: "Team archived",
        description: "You can restore it from the Archived tab.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/batches"] });
    },
    onError: (e: Error) =>
      safeToast({
        title: "Failed to archive team",
        description: e.message || "Please try again.",
      }),
  });

  const restoreMutation = useMutation({
    mutationFn: async (batchId: number) =>
      apiRequest("PATCH", `/api/batches/${batchId}/restore`),
    onSuccess: () => {
      safeToast({
        title: "Team restored",
        description: "The team is active again.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/batches"] });
    },
    onError: (e: Error) =>
      safeToast({
        title: "Failed to restore team",
        description: e.message || "Please try again.",
      }),
  });

  const openAdd = () => {
    setEditingTeam(undefined);
    setDialogOpen(true);
  };
  const openEdit = (team: BatchRow) => {
    setEditingTeam({
      id: team.id,
      name: team.name,
      ageGroup: team.ageGroup,
      ageRange: team.ageRange,
      skillLevel: team.skillLevel,
      coachId: team.coachId,
      schedule: team.schedule,
      venue: team.venue,
      maxCapacity: team.maxCapacity,
    });
    setDialogOpen(true);
  };

  return (
    <div
      className="flex-1 h-full overflow-y-auto overflow-x-hidden bg-slate-50 pb-24 md:pb-6"
      data-testid="coach-team-management-page"
    >
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-6 space-y-4 md:space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl md:text-2xl font-semibold text-slate-900">
              Team Management
            </h1>
            <p className="text-sm md:text-base text-slate-500">
              Create and manage your teams (batches).
            </p>
          </div>
          {/* Desktop-only actions (hidden on mobile; Add Team has a sticky
              FAB below md — generate-sessions is a secondary/occasional
              action, not worth a FAB of its own). */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => generateSessionsMutation.mutate()}
              disabled={generateSessionsMutation.isPending}
              data-testid="btn-generate-sessions"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {generateSessionsMutation.isPending ? "Generating…" : "Generate upcoming sessions"}
            </Button>
            <Button
              onClick={openAdd}
              data-testid="btn-add-team"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Team
            </Button>
          </div>
        </div>

        {/* Stat tiles — derived live from the real `teams` array above (not
            mock). currentEnrollment itself is still a stored counter, not
            computed here — see the "Out of scope" note above. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <MetricTile
            icon={Users}
            iconColor="text-blue-600"
            title="Total Teams"
            value={teams.length}
            testId="metric-total-teams"
          />
          <MetricTile
            icon={Award}
            iconColor="text-emerald-600"
            title="Beginner"
            value={teams.filter((t) => t.skillLevel === "Beginner").length}
            testId="metric-skill-beginner"
          />
          <MetricTile
            icon={TrendingUp}
            iconColor="text-blue-600"
            title="Intermediate"
            value={teams.filter((t) => t.skillLevel === "Intermediate").length}
            testId="metric-skill-intermediate"
          />
          <MetricTile
            icon={Calendar}
            iconColor="text-orange-600"
            title="Advanced"
            value={teams.filter((t) => t.skillLevel === "Advanced").length}
            testId="metric-skill-advanced"
          />
        </div>

        {/* Active / Archived tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2 flex gap-2 w-fit">
          <button
            onClick={() => setTab("active")}
            data-testid="tab-active-teams"
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
              tab === "active"
                ? "bg-blue-600 text-white"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            Active
          </button>
          <button
            onClick={() => setTab("archived")}
            data-testid="tab-archived-teams"
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
              tab === "archived"
                ? "bg-slate-700 text-white"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            Archived
          </button>
        </div>

        {/* Loading / error states */}
        {isLoading && (
          <div className="text-center py-12 text-slate-500" data-testid="teams-loading">
            Loading teams…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            Failed to load teams. Please refresh the page.
          </div>
        )}

        {/* Desktop table view (md and up) */}
        {!isLoading && !error && (
          <div
            className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            data-testid="coach-teams-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-900">Team</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-900">Level</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-900">Age Group</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-900">Schedule</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-900">Venue</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {teams.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-slate-500" data-testid="teams-empty">
                        {tab === "active"
                          ? "No active teams yet. Click \"Add Team\" to create one."
                          : "No archived teams."}
                      </td>
                    </tr>
                  ) : (
                    teams.map((team) => (
                      <tr
                        key={team.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/coach/teams/${team.id}`)}
                        data-testid={`team-row-${team.id}`}
                      >
                        <td className="px-4 py-3">
                          {/* Link wrapper for keyboard accessibility — Tab
                              focuses here, Enter navigates. Row onClick covers
                              mouse users clicking anywhere in the row. */}
                          <Link
                            to={`/coach/teams/${team.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block -m-1 p-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            data-testid={`link-team-name-${team.id}`}
                          >
                            <div className="font-semibold text-slate-900 hover:underline">{team.name}</div>
                            <div className="text-xs text-slate-500">
                              Age range: {team.ageRange}
                            </div>
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={cn("border", skillBadgeColor(team.skillLevel))}>
                            {team.skillLevel ?? "—"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {team.ageGroup}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {team.schedule || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {team.venue || "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openEdit(team); }}
                              data-testid={`btn-edit-team-${team.id}`}
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            {tab === "active" ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); archiveMutation.mutate(team.id); }}
                                data-testid={`btn-archive-team-${team.id}`}
                              >
                                <Archive className="h-3 w-3 mr-1" />
                                Archive
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); restoreMutation.mutate(team.id); }}
                                data-testid={`btn-restore-team-${team.id}`}
                              >
                                <ArchiveRestore className="h-3 w-3 mr-1" />
                                Restore
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mobile card view (below md) */}
        {!isLoading && !error && (
          <div className="md:hidden space-y-3">
            {teams.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500" data-testid="teams-empty-mobile">
                {tab === "active"
                  ? "No active teams yet."
                  : "No archived teams."}
              </div>
            ) : (
              teams.map((team) => (
                <div
                  key={team.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3 cursor-pointer"
                  onClick={() => navigate(`/coach/teams/${team.id}`)}
                  data-testid={`team-row-${team.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/coach/teams/${team.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="min-w-0 -m-1 p-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid={`link-team-name-${team.id}`}
                    >
                      <div className="font-semibold text-slate-900 hover:underline">{team.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {team.ageGroup} · {team.ageRange}
                      </div>
                    </Link>
                    <Badge className={cn("border shrink-0", skillBadgeColor(team.skillLevel))}>
                      {team.skillLevel ?? "—"}
                    </Badge>
                  </div>
                  {(team.schedule || team.venue) && (
                    <div className="text-xs text-slate-600 space-y-0.5">
                      {team.schedule && <div>{team.schedule}</div>}
                      {team.venue && <div>{team.venue}</div>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => { e.stopPropagation(); openEdit(team); }}
                      data-testid={`btn-edit-team-${team.id}`}
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    {tab === "active" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); archiveMutation.mutate(team.id); }}
                        data-testid={`btn-archive-team-${team.id}`}
                      >
                        <Archive className="h-3 w-3 mr-1" />
                        Archive
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); restoreMutation.mutate(team.id); }}
                        data-testid={`btn-restore-team-${team.id}`}
                      >
                        <ArchiveRestore className="h-3 w-3 mr-1" />
                        Restore
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {/* Sticky mobile FAB — fast Add-Team surface for coaches on phones.
          Uses env(safe-area-inset-bottom) so the button doesn't slide under
          the iOS Safari toolbar / Android gesture bar / future bottom-nav.
          Falls back to 16px when env() isn't available. The page also has
          pb-24 on the scroll container so the FAB doesn't overlap content
          when scrolled to the very bottom. */}
      <div
        className="md:hidden fixed right-4 z-40"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        }}
      >
        <Button
          onClick={openAdd}
          className="h-14 w-14 rounded-full shadow-lg p-0"
          data-testid="btn-add-team"
          aria-label="Add Team"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      <AddEditTeamDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        team={editingTeam}
      />
    </div>
  );
}
